import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('eva_token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setTenant(response.data.tenant);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('eva_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('eva_token', response.data.access_token);
    await fetchUser();
    return response.data;
  };

  const signup = async (email, password, tenant_name, legalAcceptance) => {
    const response = await api.post('/auth/signup', {
      email,
      password,
      tenant_name,
      ...legalAcceptance,
    });
    localStorage.setItem('eva_token', response.data.access_token);
    await fetchUser();
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.warn('Server-side logout failed; clearing local session anyway.');
    } finally {
      localStorage.removeItem('eva_token');
      setUser(null);
      setTenant(null);
      window.location.href = '/login';
    }
  };

  const value = {
    user,
    tenant,
    loading,
    login,
    signup,
    logout,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
